import { useState } from "react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { daysAgoISODate, todayISODate } from "@/stores/filterStore";
import SectionRangePicker from "@/components/SectionRangePicker";
import ChartFrame from "@/components/ChartFrame";
import InfoTooltip from "@/components/InfoTooltip";
import { endpointDot } from "@/lib/chartStyle";
import { fmt } from "@/lib/format";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import MobileStatusBanner from "@/components/MobileStatusBanner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { panelState, bedrockTone, bedrockSummary, ddbTone, ddbSummary, timeoutTone, timeoutSummary } from "@/lib/opsPanel";
import {
  ComposedChart, Bar, Line, Legend,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import Panel from "./Panel";
import { allDates } from "./finops/lib";

// DevOps 탭 — 3그룹 재구성 (2026-07-31 v4)
// 그룹: 안정성(헬스) / 속도(배포빈도+추이) / 규율(CI/CD+컨벤션)
// 가드레일: 데이터 페칭 구조·dataKey 이름(커밋/배포)·table 구조 변경 금지.
// 정직성 문구(미측정·근사치 등) 의미 유지.

function defaultRange() {
  return { from: daysAgoISODate(27), to: todayISODate() };
}

function Skel({ lines = 3 }) {
  return (
    <div className="animate-pulse space-y-2 py-1">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-4 rounded bg-ink-100" style={{ width: `${90 - i * 11}%` }} />
      ))}
    </div>
  );
}

// 그룹 라벨 제거됨 — Panel 제목으로 충분히 구분됨 (2026-07-31)

// ── DORA 배포 빈도 등급 (리드타임·변경실패율 제거) ─────────────────────────
// §0: 리드타임 카드·변경실패율 카드 제거. 배포 빈도만 등급 표시.
const DEPLOY_GRADE_TOOLTIP =
  "Elite ≥1회/일 · High ≥1회/주 · Medium ≥1회/월 · Low <1회/월 (Google DORA 2023)";

function deployGrade(perDay) {
  if (perDay == null) return null;
  if (perDay >= 1)    return { label: "Elite",  bg: "bg-emerald-100", text: "text-emerald-800" };
  if (perDay >= 0.14) return { label: "High",   bg: "bg-blue-50",     text: "text-blue-700" };
  if (perDay > 0)     return { label: "Medium", bg: "bg-amber-50",    text: "text-amber-700" };
  return                { label: "Low",    bg: "bg-red-50",      text: "text-red-600" };
}

// Elite는 초록 강조, Low 방향일수록 채도 낮아짐
function GradeBadge({ grade, tooltip }) {
  if (!grade) return null;
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className={`rounded-full px-2 py-0.5 text-[14px] font-bold ${grade.bg} ${grade.text}`}>
        {grade.label}
      </span>
      {tooltip && <InfoTooltip text={tooltip} />}
    </span>
  );
}

// ── 서비스 헬스 신호등 ─────────────────────────────────────────────────────
function HealthDot({ errPct, invocations }) {
  if (invocations === 0 || invocations == null)
    return <span className="inline-block h-2.5 w-2.5 rounded-full bg-ink-200" title="데이터 없음" />;
  if (errPct == null || errPct === 0)
    return <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" title="정상" />;
  if (errPct < 1)
    return <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" title={`오류율 ${errPct}%`} />;
  return (
    <span className="relative inline-flex h-2.5 w-2.5" title={`오류율 ${errPct}%`}>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
    </span>
  );
}

// §3: 오류율 — 정상은 텍스트, 주황/빨강만 숫자
function errRateDisplay(pct) {
  if (pct == null) return { text: "—", cls: "text-ink-300" };
  if (pct === 0)   return { text: "정상", cls: "rounded-full bg-emerald-50 px-2 py-0.5 text-[14px] font-semibold text-emerald-700" };
  if (pct < 1)     return { text: `${pct}%`, cls: "rounded-full bg-amber-50 px-2 py-0.5 text-[14px] font-semibold text-amber-700" };
  return             { text: `${pct}%`, cls: "rounded-full bg-red-50 px-2 py-0.5 text-[14px] font-semibold text-red-700" };
}

// Timeout Risk — 실제 timeout 횟수가 아니라 p95/configured-timeout 근접도(%).
// "Timeout 3건" 같은 발생 횟수 표기 금지 — 항상 "p95 / timeout N%" 형태.
function TimeoutRisk({ usagePct, risk }) {
  if (usagePct == null)
    return <span className="text-ink-300">—</span>;
  const cls = risk
    ? "rounded-full bg-red-50 px-2 py-0.5 text-[13px] font-semibold text-red-700"
    : usagePct >= 70
      ? "rounded-full bg-amber-50 px-2 py-0.5 text-[13px] font-semibold text-amber-700"
      : "text-[13px] text-ink-500 tabular-nums";
  const label = risk ? `${usagePct}% 근접` : `${usagePct}%`;
  return <span className={cls} title={`p95 / configured timeout = ${usagePct}%`}>{label}</span>;
}

// §3: p95 — 항상 초 단위, 소수 1자리
function fmtP95(ms) {
  if (ms == null) return "—";
  return `${(ms / 1000).toFixed(1)}초`;
}

// §3: 평균 소요 — 분·초 한글
function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
}

// 마지막 배포 경과
function lastDeployDate(deploys) {
  for (const d of Object.keys(deploys || {}).sort().reverse()) {
    const v = deploys[d];
    if ((v.lambda ?? 0) + (v.prod ?? 0) + (v.backoffice ?? 0) > 0) return d;
  }
  return null;
}
function sinceLabel(dateStr) {
  if (!dateStr) return null;
  const diffMs = Date.now() - new Date(dateStr + "T00:00:00+09:00").getTime();
  const hours  = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "어제" : `${days}일 전`;
}

// PR 리드타임 뱃지 — < 5분 표기
function LeadBadge({ h }) {
  if (h == null) return <span className="text-ink-300">—</span>;
  const mins  = h * 60;
  const label = mins < 5  ? "< 5분"
              : h < 1     ? `${Math.round(mins)}분`
              : h < 24    ? `${h}시간`
              :              `${Math.round(h / 24)}일`;
  const cls = h < 1   ? "bg-emerald-50 text-emerald-700"
            : h < 24  ? "bg-blue-50 text-blue-700"
            :            "bg-amber-50 text-amber-700";
  return <span className={`rounded-full px-2 py-0.5 text-[14px] font-semibold ${cls}`}>{label}</span>;
}

function isFixPr(title) {
  return /^(fix|hotfix|revert)\b/i.test((title || "").trim());
}

// Disclosure 컴포넌트 제거됨 — 모든 섹션 항상 펼침 (2026-07-31)

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────
export default function DevopsSection() {
  const [range, setRange]     = useState(defaultRange);
  const [prsOpen, setPrsOpen] = useState(false);
  const isMobile = useIsMobile();

  // 프로그레시브 로딩 — 파트별 병렬 조회. 순서·훅 이름 변경 금지.
  const ghQ   = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "github" },    s), [range.from, range.to], range);
  const depQ  = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "deploys" },   s), [range.from, range.to], range);
  const hlthQ = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "health" },    s), [range.from, range.to], range);
  const pipeQ = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "pipelines" }, s), [range.from, range.to], range);
  // Phase 2 (2026-09-22) — Bedrock / DynamoDB / 실제 Timeout. 각자 독립 조회(fail-soft).
  const brkQ  = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "bedrock" },   s), [range.from, range.to], range);
  const ddbQ  = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "ddb" },       s), [range.from, range.to], range);
  const tmoQ  = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "timeouts" },  s), [range.from, range.to], range);
  const stsQ  = useAnalyticsQuery((p, s) => analyticsApi.devops({ from: range.from, to: range.to, part: "status" },    s), [range.from, range.to], range);

  const gh        = ghQ.data?.github;
  const deploys   = depQ.data?.deploysByDay || {};
  const health    = hlthQ.data?.health || [];
  const apigw     = hlthQ.data?.apigw || [];
  const ws        = hlthQ.data?.ws || [];
  const pipelines = pipeQ.data?.pipelines || [];
  // Phase 2 — undefined(로딩) / null(조회 실패=unavailable) / 값 을 구분해서 다룬다.
  const bedrock   = brkQ.data?.bedrock;
  const ddb       = ddbQ.data?.ddb;
  const timeouts  = tmoQ.data?.timeouts;
  const opsSignals = stsQ.data?.opsSignals || [];
  const opsLevel   = stsQ.data?.opsLevel || "ok";
  const dora      = { ...(depQ.data?.dora || {}), ...(ghQ.data?.dora || {}) };
  const days      = allDates(range.from, range.to).length;

  const lastDeploy  = lastDeployDate(deploys);
  const sinceDeploy = sinceLabel(lastDeploy);

  // dataKey 이름(커밋/배포) 변경 금지
  const rhythm = allDates(range.from, range.to).map((date) => ({
    date,
    커밋: gh?.commitsByDay?.[date] ?? 0,
    배포: (deploys[date]?.lambda ?? 0) + (deploys[date]?.prod ?? 0) + (deploys[date]?.backoffice ?? 0),
  }));

  const totalCommits = rhythm.reduce((s, r) => s + r.커밋, 0);
  const totalDeploys = rhythm.reduce((s, r) => s + r.배포, 0);

  // §6 차트 스파이크 감지 — 배포 최대값의 위치
  const maxDeployDay = rhythm.reduce((best, r) => (!best || r.배포 > best.배포 ? r : best), null);

  const prsAll    = gh?.recentPrs || [];
  const prsToShow = prsAll.slice(0, prsOpen ? 30 : 8);
  const fixPrCount = prsAll.filter((p) => isFixPr(p.title)).length;

  const alertCount = health.filter((fn) => fn.errorRatePct != null && fn.errorRatePct >= 1).length;
  const warnCount  = health.filter((fn) => fn.errorRatePct != null && fn.errorRatePct > 0 && fn.errorRatePct < 1).length;

  // §1 히어로 스트립 — CI 성공률, AI 협업률
  const ciSuccessPct = (() => {
    const done = pipelines.filter((w) => w.successPct != null);
    if (!done.length) return null;
    return Math.round(done.reduce((s, w) => s + w.successPct, 0) / done.length);
  })();
  const aiPairPct    = gh?.conventions?.aiPairPct ?? null;
  const conformPct   = gh?.conventions?.conformPct ?? null;
  const deployGr     = deployGrade(dora.deploymentsPerDay);

  return (
    <div className="space-y-5">

      {/* ── 헤더 — §2 날짜 컨트롤 톤다운 ──────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink-800">DevOps 현황</p>
          <p className="mt-0.5 text-[14px] text-ink-400">{range.from} ~ {range.to} ({days}일)</p>
        </div>
        {/* §2: 날짜 컨트롤을 보조 역할로 — opacity 낮춤 */}
        <div className="opacity-70 hover:opacity-100 transition-opacity">
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
        </div>
      </div>

      {/* ── 모바일 상태 배너 + 핵심 KPI (모바일 UX Phase 1, 2026-09-21) ──────────
          Phase 2(2026-09-22)부터 판정은 backend `part=status` 의 opsSignals/opsLevel 을 쓴다.
          deterministic 이며 Bedrock 은 NOVA 귀속분만 반영된다(계정 전체는 severity 제외).
          원인 추정·LLM 없음. 정상이면 경고 문구를 만들지 않는다. desktop 은 렌더되지 않음. */}
      {isMobile && (() => {
        const errRates = health.filter((f) => f.errorRatePct != null).map((f) => f.errorRatePct);
        const maxErr = errRates.length ? Math.max(...errRates) : null;
        const p95s = health.filter((f) => f.p95Ms != null).map((f) => f.p95Ms);
        const maxP95 = p95s.length ? Math.max(...p95s) : null;
        const warnCnt = health.filter((f) => f.errorRatePct != null && f.errorRatePct > 0).length;

        return (
          <>
            <MobileStatusBanner
              domain="system"
              level={opsLevel}
              messages={opsSignals.map((s) => s.message)}
            />
            <KpiRow cols={3} mobileLayout="hero">
              <KpiCard label="최대 오류율"
                       value={maxErr != null ? `${maxErr}%` : "—"}
                       tone={maxErr != null && maxErr >= 1 ? "warn" : "brand"} hero mobileHero
                       sublabel="모니터링 대상 Lambda 중 최댓값" />
              <KpiCard label="주의 함수" value={`${warnCnt}개`}
                       tone={warnCnt > 0 ? "warn" : "default"}
                       sublabel="오류율 0% 초과 함수 수" />
              <KpiCard label="p95 최대" value={maxP95 != null ? `${fmt.int(maxP95)}ms` : "—"}
                       sublabel="구간 내 최악 p95" />
            </KpiRow>
          </>
        );
      })()}

      {/* ── Phase 2 접이식 상세 — AWS raw metric 나열이 아니라 "문제 있나"를 먼저 말한다 ── */}
      <BedrockPanel data={bedrock} loading={brkQ.loading} />
      <DdbPanel data={ddb} loading={ddbQ.loading} />
      <TimeoutPanel data={timeouts} loading={tmoQ.loading} />

      {/* ── §1 히어로 스트립 — 첫 화면(above the fold) 핵심 숫자 3개 ── */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {/* 배포 빈도 — §0: 히어로로 승격 */}
        <div className="rounded-2xl bg-emerald-600 px-5 py-4 text-white shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[14px] font-semibold uppercase tracking-wider text-emerald-100">
              배포 빈도
            </span>
            {deployGr && (
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[14px] font-bold text-white">
                {deployGr.label}
              </span>
            )}
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums leading-none">
            {dora.deploymentsPerDay != null ? `${dora.deploymentsPerDay}회/일` : "—"}
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-[14px] text-emerald-200">
              {dora.deploymentsPerDay != null ? `총 ${totalDeploys}회 · ${days}일` : "배포 이벤트 조회 대기"}
            </span>
            <InfoTooltip text={DEPLOY_GRADE_TOOLTIP} />
          </div>
          {sinceDeploy && (
            <div className="mt-2 border-t border-emerald-500 pt-2 text-[14px] text-emerald-200">
              마지막 배포 <span className="font-semibold text-white">{sinceDeploy}</span>
            </div>
          )}
        </div>

        {/* CI 성공률 */}
        <div className="rounded-2xl bg-white px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
          <span className="text-[14px] font-semibold uppercase tracking-wider text-ink-600">CI 성공률</span>
          <div className="mt-2 text-2xl font-bold tabular-nums leading-none text-ink-900">
            {pipeQ.loading && !pipeQ.data ? (
              <span className="text-ink-200">—</span>
            ) : ciSuccessPct != null ? (
              `${ciSuccessPct}%`
            ) : (
              <span className="text-ink-300 text-xl">측정 중</span>
            )}
          </div>
          <p className="mt-1.5 text-[14px] text-ink-400">
            활성 워크플로 평균
          </p>
          {pipelines.length > 0 && (
            <div className="mt-2 border-t border-ink-100 pt-2 text-[14px] text-ink-400">
              워크플로 {pipelines.length}개 · 활성 {pipelines.filter(w => w.state === "active").length}개
            </div>
          )}
        </div>

        {/* AI 협업률 */}
        <div className="rounded-2xl bg-white px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
          <span className="text-[14px] font-semibold uppercase tracking-wider text-ink-600">AI 협업률</span>
          <div className="mt-2 text-2xl font-bold tabular-nums leading-none text-ink-900">
            {ghQ.loading && !ghQ.data ? (
              <span className="text-ink-200">—</span>
            ) : aiPairPct != null ? (
              `${aiPairPct}%`
            ) : (
              <span className="text-ink-300 text-xl">—</span>
            )}
          </div>
          <p className="mt-1.5 text-[14px] text-ink-400">
            Co-Authored-By: Claude
          </p>
          {conformPct != null && (
            <div className="mt-2 border-t border-ink-100 pt-2 text-[14px] text-ink-400">
              컨벤션 준수율 <span className="font-semibold text-ink-600">{conformPct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          그룹 1 — 안정성  (서비스 헬스)
      ════════════════════════════════════════════════════════════════ */}
      {/* ════ 그룹 1 — 안정성 ════ */}

      <Panel
        title="서비스 헬스"
        action={
          hlthQ.data ? (
            <span className="flex items-center gap-1.5">
              {alertCount > 0 ? (
                <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[14px] font-semibold text-red-700">
                  🔴 Lambda 오류 {alertCount}개
                </span>
              ) : warnCount > 0 ? (
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[14px] font-semibold text-amber-700">
                  ⚠ Lambda 주의 {warnCount}개
                </span>
              ) : (
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[14px] font-semibold text-emerald-700">
                  ✓ Lambda 정상
                </span>
              )}
              <InfoTooltip text="CloudWatch Lambda 지표 기준 · 오류율 0.1% 이상 주의 · 1% 이상 오류 (API Gateway·WebSocket은 아래 별도 패널)" />
            </span>
          ) : null
        }
      >
        {hlthQ.loading && !hlthQ.data ? <Skel lines={5} /> :
         health.length === 0 ? (
          <p className="text-sm text-ink-400">CloudWatch 조회 실패 — IAM 권한을 확인하세요.</p>
        ) : (
          <>
            {/* §4: 신호등 색 범례 */}
            <div className="mb-3 flex items-center gap-3 text-[14px] text-ink-400">
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />정상</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-amber-400" />주의 (오류율 0.1%+)</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-red-500" />오류 (1%+)</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-ink-200" />데이터 없음</span>
            </div>
            <div className="overflow-x-auto">
              {/* table 구조 유지 — Layout.jsx 전역 더블클릭 복사 리스너 의존 */}
              <table className="w-full min-w-[680px] table-fixed text-[14px]">
                <colgroup>
                  <col className="w-7" />        {/* 신호등 */}
                  <col />                         {/* 서비스명 — 나머지 공간 */}
                  <col className="w-24" />        {/* 호출 */}
                  <col className="w-24" />        {/* 오류 */}
                  <col className="w-24" />        {/* 오류율 */}
                  <col className="w-20" />        {/* Throttles */}
                  <col className="w-24" />        {/* p95 */}
                  <col className="w-28" />        {/* Timeout Risk */}
                </colgroup>
                <thead>
                  <tr className="border-b border-ink-100 text-[14px] uppercase tracking-wide text-ink-400">
                    <th className="pb-2.5 font-medium" />
                    <th className="pb-2.5 text-left font-medium" />
                    <th className="pb-2.5 text-center font-medium">호출</th>
                    <th className="pb-2.5 text-center font-medium">오류</th>
                    <th className="pb-2.5 text-center font-medium">오류율</th>
                    <th className="pb-2.5 text-center font-medium">
                      <span className="inline-flex items-center justify-center gap-1">
                        Throttles
                        <InfoTooltip text="동시성·쿼터 제한으로 호출이 거부된 횟수입니다. Timeout과 다른 개념입니다." />
                      </span>
                    </th>
                    <th className="pb-2.5 text-center font-medium">
                      <span className="inline-flex items-center justify-center gap-1">
                        p95
                        <InfoTooltip text="websocket 등 연결 유지형은 응답 지연이 아니라 연결 시간입니다" />
                      </span>
                    </th>
                    <th className="pb-2.5 text-center font-medium">
                      <span className="inline-flex items-center justify-center gap-1">
                        Timeout Risk
                        <InfoTooltip text="실제 timeout 발생 횟수가 아니라 p95가 설정 timeout에 얼마나 근접했는지(%)입니다. 90% 이상이면 위험." />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {health.map((fn) => {
                    const isBad = fn.errorRatePct != null && fn.errorRatePct >= 1;
                    const { text: errText, cls: errCls } = errRateDisplay(fn.errorRatePct);
                    return (
                      <tr key={fn.fn} className={`border-t border-ink-100 ${isBad ? "bg-red-50/30" : ""}`}>
                        <td className="py-3">
                          <HealthDot errPct={fn.errorRatePct} invocations={fn.invocations} />
                        </td>
                        <td className="py-3">
                          <span className="font-medium text-ink-800">{fn.name}</span>
                          <span className="ml-2 hidden truncate font-mono text-[13px] text-ink-400 sm:inline">{fn.fn}</span>
                        </td>
                        <td className="py-3 text-center tabular-nums text-ink-500">{fmt.int(fn.invocations)}</td>
                        <td className="py-3 text-center tabular-nums">
                          <span className={fn.errors > 0 ? "font-semibold text-red-600" : "text-ink-300"}>
                            {fmt.int(fn.errors)}
                          </span>
                        </td>
                        <td className="py-3 text-center">
                          <span className={errCls}>{errText}</span>
                        </td>
                        <td className="py-3 text-center tabular-nums">
                          <span className={fn.throttles > 0 ? "font-semibold text-amber-600" : "text-ink-300"}>
                            {fmt.int(fn.throttles ?? 0)}
                          </span>
                        </td>
                        <td className="py-3 text-center tabular-nums text-ink-500">{fmtP95(fn.p95Ms)}</td>
                        <td className="py-3 text-center">
                          <TimeoutRisk usagePct={fn.timeoutUsagePct} risk={fn.timeoutRisk} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {/* ════ 그룹 1 — 안정성 (확장): API Gateway 5xx ════ */}
      <Panel
        title="API Gateway"
        action={
          hlthQ.data ? (
            (() => {
              const total5xx = apigw.reduce((s, a) => s + (a.errors5xx || 0), 0);
              return total5xx > 0 ? (
                <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[14px] font-semibold text-red-700">🔴 5xx {fmt.int(total5xx)}건</span>
              ) : (
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[14px] font-semibold text-emerald-700">정상</span>
              );
            })()
          ) : null
        }
      >
        {hlthQ.loading && !hlthQ.data ? <Skel lines={2} /> :
         apigw.length === 0 ? (
          <p className="text-sm text-ink-400">CloudWatch 조회 실패 — IAM 권한을 확인하세요.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] table-fixed text-[14px]">
              <colgroup>
                <col className="w-7" />
                <col />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-24" />
              </colgroup>
              <thead>
                <tr className="border-b border-ink-100 text-[14px] uppercase tracking-wide text-ink-400">
                  <th className="pb-2.5 font-medium" />
                  <th className="pb-2.5 text-left font-medium" />
                  <th className="pb-2.5 text-center font-medium">요청 수</th>
                  <th className="pb-2.5 text-center font-medium">5xx</th>
                  <th className="pb-2.5 text-center font-medium">5xx율</th>
                </tr>
              </thead>
              <tbody>
                {apigw.map((a) => {
                  const bad = (a.errors5xx || 0) > 0;
                  return (
                    <tr key={a.api} className={`border-t border-ink-100 ${bad ? "bg-red-50/30" : ""}`}>
                      <td className="py-3">
                        <span className={`inline-block h-2.5 w-2.5 rounded-full ${bad ? "bg-red-500" : "bg-emerald-500"}`} />
                      </td>
                      <td className="py-3">
                        <span className="font-medium text-ink-800">{a.name}</span>
                        <span className="ml-2 hidden truncate font-mono text-[13px] text-ink-400 sm:inline">{a.api}</span>
                      </td>
                      <td className="py-3 text-center tabular-nums text-ink-500">{fmt.int(a.count ?? 0)}</td>
                      <td className="py-3 text-center tabular-nums">
                        <span className={bad ? "font-semibold text-red-600" : "text-ink-300"}>{fmt.int(a.errors5xx ?? 0)}</span>
                      </td>
                      <td className="py-3 text-center tabular-nums text-ink-500">
                        {a.errorRatePct != null ? `${a.errorRatePct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ════ 그룹 1 — 안정성 (확장): WebSocket 오류 ════ */}
      <Panel
        title="WebSocket"
        action={
          hlthQ.data ? (
            (() => {
              const totalErr = ws.reduce((s, w) => s + (w.integrationError || 0) + (w.executionError || 0) + (w.clientError || 0), 0);
              return totalErr > 0 ? (
                <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[14px] font-semibold text-red-700">🔴 오류 {fmt.int(totalErr)}건</span>
              ) : (
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[14px] font-semibold text-emerald-700">정상</span>
              );
            })()
          ) : null
        }
      >
        {hlthQ.loading && !hlthQ.data ? <Skel lines={2} /> :
         ws.length === 0 ? (
          <p className="text-sm text-ink-400">CloudWatch 조회 실패 — IAM 권한을 확인하세요.</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-[13px] text-ink-400">
              <span>Integration Error: 백엔드(Lambda) 통합 응답 오류</span>
              <span>Execution Error: 게이트웨이 실행 오류</span>
              <span>Client Error: 클라이언트 요청 오류</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] table-fixed text-[14px]">
                <colgroup>
                  <col className="w-7" />
                  <col />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-28" />
                </colgroup>
                <thead>
                  <tr className="border-b border-ink-100 text-[14px] uppercase tracking-wide text-ink-400">
                    <th className="pb-2.5 font-medium" />
                    <th className="pb-2.5 text-left font-medium" />
                    <th className="pb-2.5 text-center font-medium">Integration Error</th>
                    <th className="pb-2.5 text-center font-medium">Execution Error</th>
                    <th className="pb-2.5 text-center font-medium">Client Error</th>
                  </tr>
                </thead>
                <tbody>
                  {ws.map((w) => {
                    const ie = w.integrationError ?? 0, ee = w.executionError ?? 0, ce = w.clientError ?? 0;
                    const bad = ie > 0 || ee > 0;
                    return (
                      <tr key={w.api} className={`border-t border-ink-100 ${bad ? "bg-red-50/30" : ""}`}>
                        <td className="py-3">
                          <span className={`inline-block h-2.5 w-2.5 rounded-full ${bad ? "bg-red-500" : "bg-emerald-500"}`} />
                        </td>
                        <td className="py-3">
                          <span className="font-medium text-ink-800">{w.name}</span>
                          <span className="ml-2 hidden truncate font-mono text-[13px] text-ink-400 sm:inline">{w.api}</span>
                        </td>
                        <td className="py-3 text-center tabular-nums">
                          <span className={ie > 0 ? "font-semibold text-red-600" : "text-ink-300"}>{fmt.int(ie)}</span>
                        </td>
                        <td className="py-3 text-center tabular-nums">
                          <span className={ee > 0 ? "font-semibold text-red-600" : "text-ink-300"}>{fmt.int(ee)}</span>
                        </td>
                        <td className="py-3 text-center tabular-nums">
                          <span className={ce > 0 ? "font-semibold text-amber-600" : "text-ink-300"}>{fmt.int(ce)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {/* ════════════════════════════════════════════════════════════════
          그룹 2 — 속도  (배포 빈도 + 커밋·배포 추이)
      ════════════════════════════════════════════════════════════════ */}
      {/* ════ 그룹 2 — 속도 ════ */}

      {/* §0: DORA 패널 제거 — 배포 빈도는 히어로로, MTTR은 맥락으로 흡수 */}
      {/* MTTR 맥락 — 비중 낮춰 빈 공간이 도드라지지 않게 */}
      <div className="flex items-center gap-2 rounded-xl border border-ink-100 bg-white px-4 py-2.5">
        <span className="text-[14px] font-medium text-ink-500">MTTR</span>
        <span className="text-[14px] text-ink-400">—</span>
        <span className="text-[14px] text-ink-300">장애 기록 구조화 전 — 추정치 미표시</span>
        <InfoTooltip text="Mean Time To Recovery: 장애 발생~복구까지 평균 소요 시간. 장애 로그 구조화 후 자동 집계 예정." />
      </div>

      {/* 커밋·배포 추이 */}
      <Panel
        title="커밋 · 배포 추이"
        action={
          sinceDeploy && (
            <span className="text-[14px] text-ink-500">
              마지막 배포 <span className="font-semibold text-ink-800">{sinceDeploy}</span>
            </span>
          )
        }
      >
        {(ghQ.loading && !ghQ.data) || (depQ.loading && !depQ.data) ? <Skel lines={7} /> : (
          <>
            <p className="mb-3 text-[14px] text-ink-400">
              막대 = 커밋 수 · 선 = 배포 횟수. 커밋이 많고 배포도 잦은 날이 개발 속도의 정점.
            </p>
            <ChartFrame base="h-52 sm:h-60" tall="h-72 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={rhythm} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#f0f0ee" />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} minTickGap={28}
                         tick={{ fill: "#8e8e8a", fontSize: 12 }} axisLine={false} tickLine={false} tickMargin={8} />
                  {/* §6: y축 스케일 — 최대값에 10% 패딩만 줘서 여백 과다 해소 */}
                  <YAxis tick={{ fill: "#8e8e8a", fontSize: 12 }} width={28} axisLine={false} tickLine={false}
                         domain={[0, (max) => Math.ceil(max * 1.15)]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(15,23,42,0.12)", fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
                  {/* §6: 배포 스파이크 주석 */}
                  {maxDeployDay && maxDeployDay.배포 > 5 && (
                    <ReferenceLine x={maxDeployDay.date} stroke="#10b981" strokeDasharray="3 3"
                      label={{ value: "배포 스파이크", position: "top", fontSize: 12, fill: "#10b981" }} />
                  )}
                  {/* dataKey 이름 변경 금지 */}
                  <Bar dataKey="커밋" fill="#e2e8f0" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="배포" stroke="#10b981" strokeWidth={2.5}
                        dot={endpointDot("#10b981", rhythm.length)} activeDot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartFrame>
          </>
        )}
      </Panel>

      {/* 최근 PR */}
      <Panel
        title="최근 PR"
        action={
          <span className="flex items-center gap-2">
            {fixPrCount > 0 && (
              <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[14px] font-semibold text-red-700">
                이번 기간 fix형 PR {fixPrCount}건
              </span>
            )}
            <span className="text-[14px] text-ink-400">리드타임 = PR 생성→머지</span>
          </span>
        }
      >
        {ghQ.loading && !ghQ.data ? <Skel lines={5} /> : (
          <>
            <div className="overflow-x-auto">
              {/* table 구조 유지 */}
              <table className="w-full min-w-[560px] table-fixed text-[14px]">
                <colgroup>
                  <col className="w-20" />   {/* PR 번호 */}
                  <col />                     {/* 제목 — 나머지 공간 */}
                  <col className="w-20" />   {/* 상태 */}
                  <col className="w-24" />   {/* 생성일 */}
                  <col className="w-24" />   {/* 리드타임 */}
                </colgroup>
                <thead>
                  <tr className="border-b border-ink-100 text-[14px] uppercase tracking-wide text-ink-400">
                    <th className="pb-2.5 text-center font-medium">PR</th>
                    <th className="pb-2.5 text-left font-medium">제목</th>
                    <th className="pb-2.5 text-center font-medium">상태</th>
                    <th className="pb-2.5 text-center font-medium">생성일</th>
                    <th className="pb-2.5 text-center font-medium">리드타임</th>
                  </tr>
                </thead>
                <tbody>
                  {prsToShow.map((p) => {
                    const isFix = isFixPr(p.title);
                    return (
                      <tr key={p.number} className={`border-t border-ink-100 hover:bg-ink-50/40 ${isFix ? "bg-red-50/20" : ""}`}>
                        <td className="py-2.5 text-center tabular-nums text-ink-400">#{p.number}</td>
                        <td className="truncate py-2.5 pr-3">
                          {isFix && (
                            <span className="mr-1.5 inline-block rounded bg-red-100 px-1 py-px text-[13px] font-bold uppercase text-red-600">fix</span>
                          )}
                          <span className={isFix ? "text-red-800" : "text-ink-800"}>{p.title}</span>
                        </td>
                        <td className="py-2.5 text-center">
                          {p.merged
                            ? <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[13px] font-semibold text-purple-700">머지</span>
                            : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[13px] font-semibold text-emerald-700">오픈</span>}
                        </td>
                        <td className="py-2.5 text-center tabular-nums text-ink-400">{p.createdAt.slice(5)}</td>
                        <td className="py-2.5 text-center">
                          {p.merged ? <LeadBadge h={p.leadH} /> : <span className="text-ink-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {prsAll.length > 8 && (
              <button type="button" onClick={() => setPrsOpen((v) => !v)}
                      className="mt-2 w-full rounded-lg bg-ink-50 py-2.5 text-[14px] font-medium text-ink-500 hover:bg-ink-100">
                {prsOpen ? "접기" : `더보기 (${prsAll.length - 8}건)`}
              </button>
            )}
            {!gh && <p className="pt-3 text-sm text-ink-400">GitHub 조회 실패 — 토큰을 확인하세요.</p>}
          </>
        )}
      </Panel>

      {/* ════════════════════════════════════════════════════════════════
          그룹 3 — 규율  (CI/CD + 커밋 컨벤션)
      ════════════════════════════════════════════════════════════════ */}
      {/* ════ 그룹 3 — 규율 ════ */}

      {/* CI/CD 파이프라인 */}
      <Panel
        title="CI/CD 파이프라인"
        action={
          ciSuccessPct != null ? (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[14px] font-semibold text-emerald-700">
              성공률 {ciSuccessPct}%
            </span>
          ) : null
        }
      >
          {pipeQ.loading && !pipeQ.data ? <Skel lines={3} /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[540px] table-fixed text-[14px]">
                <colgroup>
                  <col />                     {/* 워크플로 — 나머지 공간 */}
                  <col className="w-36" />   {/* 상태 */}
                  <col className="w-24" />   {/* 성공률 */}
                  <col className="w-24" />   {/* 평균 소요 */}
                  <col className="w-24" />   {/* 마지막 실행 */}
                </colgroup>
                <thead>
                  <tr className="border-b border-ink-100 text-[14px] uppercase tracking-wide text-ink-400">
                    <th className="pb-2.5 text-left font-medium">워크플로</th>
                    <th className="pb-2.5 text-center font-medium">상태</th>
                    <th className="pb-2.5 text-center font-medium">성공률</th>
                    <th className="pb-2.5 text-center font-medium">평균 소요</th>
                    <th className="pb-2.5 text-center font-medium">마지막 실행</th>
                  </tr>
                </thead>
                <tbody>
                  {(pipelines || []).map((w) => {
                    const isBlank = w.successPct == null && w.avgSec == null && !w.lastRunAt;
                    return (
                      <tr key={w.name} className={`border-t border-ink-100 hover:bg-ink-50/40 ${isBlank ? "opacity-40" : ""}`}>
                        <td className="py-2.5 text-ink-800">{w.name}</td>
                        <td className="py-2.5 text-center">
                          {w.state === "active" ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[14px] font-medium text-emerald-700">활성</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[14px] font-medium text-amber-700">
                              꺼짐 · 스피드 모드
                              <InfoTooltip text="출시 전 의도적 비활성 · 재가동은 ci-toggle.sh on" />
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-center tabular-nums">
                          {isBlank ? (
                            <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[14px] text-ink-400">미실행</span>
                          ) : w.successPct != null ? `${w.successPct}%` : "—"}
                        </td>
                        <td className="py-2.5 text-center tabular-nums">
                          {isBlank ? <span className="text-ink-200">—</span> : fmtDuration(w.avgSec)}
                        </td>
                        <td className="py-2.5 text-center tabular-nums text-ink-400">
                          {w.lastRunAt || (isBlank ? <span className="text-ink-200">—</span> : "—")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!pipeQ.data?.pipelines && (
                <p className="pt-2 text-sm text-ink-400">GitHub Actions 조회 실패.</p>
              )}
            </div>
          )}
      </Panel>

      {/* 커밋 컨벤션 */}
      <Panel
        title="커밋 컨벤션"
        action={
          conformPct != null ? (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[14px] font-semibold text-brand-700">
              준수율 {conformPct}%
            </span>
          ) : null
        }
      >
          {ghQ.loading && !ghQ.data ? <Skel lines={4} /> : (
            <>
              {/* §4: 카드 스타일 통일 — bg-ink-50/70 단일 베이스, 색 점으로만 구분 */}
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                <div className="rounded-xl bg-ink-50/70 p-3">
                  <div className="flex items-center gap-1.5 text-[14px] font-medium text-ink-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-ink-400" />컨벤션 준수율
                  </div>
                  <div className="mt-1.5 text-lg font-semibold tabular-nums text-ink-900">
                    {gh?.conventions?.conformPct != null ? `${gh.conventions.conformPct}%` : "—"}
                  </div>
                  {/* §6: sublabel 대비 강화 */}
                  <div className="mt-0.5 text-[14px] text-ink-500">feat/fix/docs… 타입 접두</div>
                </div>
                <div className="rounded-xl bg-ink-50/70 p-3">
                  <div className="flex items-center gap-1.5 text-[14px] font-medium text-ink-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />기록물 커밋
                  </div>
                  <div className="mt-1.5 text-lg font-semibold tabular-nums text-ink-900">
                    {gh?.conventions?.recordPct != null ? `${gh.conventions.recordPct}%` : "—"}
                  </div>
                  <div className="mt-0.5 text-[14px] text-ink-500">[Before/After]·[검증]</div>
                </div>
                <div className="rounded-xl bg-ink-50/70 p-3">
                  <div className="flex items-center gap-1.5 text-[14px] font-medium text-ink-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />AI 협업
                  </div>
                  <div className="mt-1.5 text-lg font-semibold tabular-nums text-ink-900">
                    {gh?.conventions?.aiPairPct != null ? `${gh.conventions.aiPairPct}%` : "—"}
                  </div>
                  <div className="mt-0.5 text-[14px] text-ink-500">Co-Authored-By: Claude</div>
                </div>
              </div>

              {(gh?.conventions?.typeDist || []).length > 0 && (
                <div>
                  <p className="mb-2 text-[14px] font-semibold uppercase tracking-wide text-ink-400">타입 분포</p>
                  <div className="space-y-1.5">
                    {(() => {
                      const dist  = gh.conventions.typeDist;
                      const max   = Math.max(...dist.map((x) => x.n), 1);
                      const total = dist.reduce((s, x) => s + x.n, 0);
                      return dist.map((x) => {
                        const pct = total > 0 ? ((x.n / total) * 100).toFixed(0) : 0;
                        return (
                          <div key={x.type} className="flex items-center gap-2 text-[14px]">
                            <span className="w-20 shrink-0 font-mono font-medium text-ink-600">{x.type}</span>
                            <div className="h-4 flex-1 overflow-hidden rounded bg-ink-100">
                              <div className="h-full rounded bg-brand-500/60" style={{ width: `${(x.n / max) * 100}%` }} />
                            </div>
                            <span className="w-20 shrink-0 text-right tabular-nums text-ink-600">
                              {x.n}건 <span className="text-ink-400">({pct}%)</span>
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
              {!gh && <p className="text-sm text-ink-400">GitHub 조회 실패 — 토큰을 확인하세요.</p>}
            </>
          )}
      </Panel>

      {/* §8: DORA 맥락 카피 — 평가자를 위한 접기 */}
      <details className="group">
        <summary className="cursor-pointer select-none text-[14px] text-ink-400 hover:text-ink-600 [&::-webkit-details-marker]:hidden">
          DORA가 뭔가요? ▾
        </summary>
        <div className="mt-2 rounded-xl border border-ink-100 bg-white p-4 text-[14px] leading-relaxed text-ink-500">
          <p className="font-semibold text-ink-700">Google DORA (DevOps Research &amp; Assessment)</p>
          <p className="mt-1">구글이 수만 개 팀을 분석해 개발 성과와 비즈니스 성과 간 상관관계를 증명한 연구입니다. 4개 지표(배포 빈도·리드타임·변경 실패율·MTTR)로 팀의 소프트웨어 전달 능력을 측정합니다.</p>
          <ul className="mt-2 space-y-1 text-ink-400">
            <li><span className="font-medium text-ink-600">배포 빈도</span> — Elite 기준 하루 1회 이상. 자주 배포할수록 위험이 작게 분산됩니다.</li>
            <li><span className="font-medium text-ink-600">MTTR</span> — 장애 발생 후 복구까지 시간. 현재 장애 기록 구조화 전으로 미측정.</li>
          </ul>
        </div>
      </details>

    </div>
  );
}

// ══ Phase 2 접이식 패널 (2026-09-22) ═════════════════════════════════════════
// 공통 원칙: 결론을 먼저 한 줄로 말하고, 숫자는 펼쳤을 때 본다.
//   · data === undefined → 로딩
//   · data === null      → 조회 실패(unavailable). "0건"이라고 말하지 않는다.
//   · 정상(0)            → "이상 없음"으로 간결히.

function OpsPanel({ title, summary, tone = "ok", children }) {
  const dot = { ok: "bg-emerald-500", warn: "bg-amber-500", critical: "bg-red-500", na: "bg-ink-300" }[tone];
  return (
    <details className="group rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]">
      <summary className="flex cursor-pointer items-center gap-2 px-4 py-3.5 text-sm font-semibold text-ink-800 [&::-webkit-details-marker]:hidden">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
        {title}
        <span className="ml-auto flex items-center gap-2 text-[12px] font-normal text-ink-500">
          {summary}
          <span className="text-ink-400 transition-transform group-open:rotate-90">▶</span>
        </span>
      </summary>
      <div className="px-4 pb-4">{children}</div>
    </details>
  );
}

function NaBody({ text }) {
  return <p className="text-[13px] text-ink-500">{text}</p>;
}

// ── AI 호출 오류 (Bedrock) ───────────────────────────────────────────────────
// NOVA 귀속과 계정 전체를 시각적으로 분리한다 — 계정 전체는 타 프로젝트가 섞여 있어
// NOVA 상태 판정에 쓰지 않는다(라벨로 명시).
function BedrockPanel({ data, loading }) {
  const st = panelState(data, loading);
  if (st === "loading") return <OpsPanel title="AI 호출 오류" summary="불러오는 중…" tone="na" />;
  if (st === "unavailable") {
    return (
      <OpsPanel title="AI 호출 오류" summary="조회 실패" tone="na">
        <NaBody text="CloudWatch(AWS/Bedrock) 조회에 실패했습니다. 오류가 0건이라는 뜻이 아닙니다." />
      </OpsPanel>
    );
  }
  const n = data.nova || {};
  const a = data.account || {};
  const u = data.unattributed || {};
  const sRate = n.serverErrorRatePct, cRate = n.clientErrorRatePct;
  const tone = bedrockTone(n);          // NOVA 귀속만 반영(계정 전체 제외)
  const summary = bedrockSummary(n);

  const Row = ({ label, value, sub }) => (
    <div className="flex items-baseline justify-between border-b border-ink-50 py-1.5 last:border-0">
      <span className="text-[13px] text-ink-600">{label}</span>
      <span className="text-right">
        <span className="text-[13px] font-semibold tabular-nums text-ink-800">{value}</span>
        {sub && <span className="ml-1.5 text-[11px] text-ink-400">{sub}</span>}
      </span>
    </div>
  );
  return (
    <OpsPanel title="AI 호출 오류" summary={summary} tone={tone}>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-600">NOVA 모델 (상태 판정 기준)</p>
      <Row label="호출" value={fmt.int(n.invocations || 0)} sub={`모델 ${n.modelCount || 0}종`} />
      <Row label="서버 오류 (5xx 상당)" value={`${fmt.int(n.serverErrors || 0)}건`} sub={sRate != null ? `${sRate}%` : "비율 산출 불가"} />
      <Row label="요청 오류 (4xx 상당)" value={`${fmt.int(n.clientErrors || 0)}건`} sub={cRate != null ? `${cRate}%` : "비율 산출 불가"} />
      <Row label="스로틀" value={`${fmt.int(n.throttles || 0)}건`} />
      {(n.models || []).length > 0 && (
        <div className="mt-2">
          {n.models.map((m) => (
            <Row key={m.modelId} label={m.modelId.replace(/^us\.anthropic\.|^apac\.amazon\./, "")}
                 value={`${fmt.int(m.invocations)}회`}
                 sub={`서버 ${m.serverErrorRatePct ?? 0}% · 요청 ${m.clientErrorRatePct ?? 0}%`} />
          ))}
        </div>
      )}
      <p className="mb-1.5 mt-4 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
        계정 전체 (타 프로젝트 포함 — 참고용, 상태 판정 제외)
      </p>
      <Row label="호출" value={fmt.int(a.invocations || 0)} />
      <Row label="서버 오류" value={`${fmt.int(a.serverErrors || 0)}건`} sub={a.serverErrorRatePct != null ? `${a.serverErrorRatePct}%` : ""} />
      <Row label="요청 오류" value={`${fmt.int(a.clientErrors || 0)}건`} sub={a.clientErrorRatePct != null ? `${a.clientErrorRatePct}%` : ""} />
      <Row label="판별 불가분" value={`${fmt.int(u.invocations || 0)}회`} sub="NOVA 귀속 아님" />
    </OpsPanel>
  );
}

// ── 데이터베이스 스로틀 (DynamoDB) ────────────────────────────────────────────
function DdbPanel({ data, loading }) {
  const st = panelState(data, loading);
  if (st === "loading") return <OpsPanel title="데이터베이스 스로틀" summary="불러오는 중…" tone="na" />;
  if (st === "unavailable") {
    return (
      <OpsPanel title="데이터베이스 스로틀" summary="조회 실패" tone="na">
        <NaBody text="CloudWatch(AWS/DynamoDB) 조회에 실패했습니다. 스로틀이 0건이라는 뜻이 아닙니다." />
      </OpsPanel>
    );
  }
  const total = data.totalThrottled || 0;
  return (
    <OpsPanel title="데이터베이스 스로틀" summary={ddbSummary(data)} tone={ddbTone(data)}>
      {total === 0 ? (
        <p className="text-[13px] text-ink-500">핵심 테이블 {data.tableCount}개 모두 스로틀 0건입니다.</p>
      ) : (
        <div>
          {data.tables.filter((t) => t.throttledRequests > 0).map((t) => (
            <div key={t.table} className="flex items-baseline justify-between border-b border-ink-50 py-1.5 last:border-0">
              <span className="text-[13px] text-ink-600">{t.label}<span className="ml-1.5 font-mono text-[11px] text-ink-400">{t.table}</span></span>
              <span className="text-[13px] font-semibold tabular-nums text-red-600">
                {fmt.int(t.throttledRequests)}건
                {t.readThrottleEvents != null && (
                  <span className="ml-1.5 text-[11px] font-normal text-ink-400">R {t.readThrottleEvents} · W {t.writeThrottleEvents}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </OpsPanel>
  );
}

// ── 실제 함수 Timeout ─────────────────────────────────────────────────────────
// metric filter 미설정을 "0건"으로 표시하지 않는다(계측 공백을 정상으로 오인하면 장애를 놓친다).
function TimeoutPanel({ data, loading }) {
  const st = panelState(data, loading);
  if (st === "loading") return <OpsPanel title="실제 함수 Timeout" summary="불러오는 중…" tone="na" />;
  if (st === "unavailable") {
    return (
      <OpsPanel title="실제 함수 Timeout" summary="조회 실패" tone="na">
        <NaBody text="CloudWatch 조회에 실패했습니다. timeout 이 0건이라는 뜻이 아닙니다." />
      </OpsPanel>
    );
  }
  const notCfg = data.notConfigured || [];
  const allMissing = notCfg.length === (data.functions || []).length;
  const total = data.total || 0;
  const tone = timeoutTone(data);
  const summary = timeoutSummary(data);
  return (
    <OpsPanel title="실제 함수 Timeout" summary={summary} tone={tone}>
      {allMissing && (
        <p className="mb-2 rounded-lg bg-ink-50 px-3 py-2 text-[12px] text-ink-600">
          아직 로그 계측(metric filter)이 만들어지지 않았습니다 — 0건이 아니라 <b>측정되지 않음</b>입니다.
        </p>
      )}
      {(data.functions || []).map((f) => (
        <div key={f.fn} className="flex items-baseline justify-between border-b border-ink-50 py-1.5 last:border-0">
          <span className="text-[13px] text-ink-600">{f.name}</span>
          <span className={`text-[13px] font-semibold tabular-nums ${f.timeouts ? "text-red-600" : "text-ink-800"}`}>
            {f.configured ? `${fmt.int(f.timeouts)}건` : <span className="font-normal text-ink-400">미계측</span>}
          </span>
        </div>
      ))}
    </OpsPanel>
  );
}
